/**
 * Oneat motion + performance layer.
 *
 * A dependency-free port of the four things that make the IMECO theme read as
 * premium: scroll-reveal, word-staggered headings, an image that fades in from
 * a skeleton instead of popping, and hover-prefetched navigation.
 *
 * Why it is one file and not a library: IMECO buys all of this with Motion One
 * plus Splitting.js inside a 250KB `vendor.js`. Everything here is the Web
 * Animations API and one shared IntersectionObserver, so the whole layer is a
 * few KB and adds no render-blocking work.
 *
 * Why it is an Oneat file: a Horizon update replaces every file Horizon ships.
 * This is loaded from `snippets/oneat-motion.liquid`, which `oneat-theme`
 * renders, so an update cannot take it away. See CLAUDE.md rule 1.
 *
 * Nothing here is required for content to be readable. `html.oneat-motion` is
 * set by an inline script in the snippet and is the only thing that arms the
 * hidden-before-reveal CSS, so if this module fails to load, or the visitor
 * asked for reduced motion, every element is simply visible.
 */

const html = document.documentElement;
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
const DESIGN_MODE = Boolean(window.Shopify && window.Shopify.designMode);

/* Easing shared by every reveal. Matches IMECO's [0.16, 1, 0.3, 1] - a long
   ease-out that settles rather than stops, which is most of why its motion
   reads as expensive rather than bouncy. */
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

const KEYFRAMES = {
  'fade-in': [{ opacity: 0 }, { opacity: 1 }],
  'fade-up': [
    { opacity: 0, transform: 'translateY(min(2rem, 90%))' },
    { opacity: 1, transform: 'translateY(0)' },
  ],
  'fade-up-large': [
    { opacity: 0, transform: 'translateY(90%)' },
    { opacity: 1, transform: 'translateY(0)' },
  ],
  'fade-left': [
    { opacity: 0, transform: 'translateX(2rem)' },
    { opacity: 1, transform: 'translateX(0)' },
  ],
  'fade-right': [
    { opacity: 0, transform: 'translateX(-2rem)' },
    { opacity: 1, transform: 'translateX(0)' },
  ],
  'zoom-out': [{ transform: 'scale(1.12)' }, { transform: 'scale(1)' }],
  'zoom-in': [
    { opacity: 0, transform: 'scale(0.94)' },
    { opacity: 1, transform: 'scale(1)' },
  ],
};

const DURATIONS = {
  'fade-in': 900,
  'fade-up': 900,
  'fade-up-large': 1000,
  'fade-left': 900,
  'fade-right': 900,
  'zoom-out': 1300,
  'zoom-in': 900,
};

/* --------------------------- scroll reveal --------------------------- */

/**
 * One observer for the whole page rather than one per element. A section with
 * 24 revealing cards costs a single observer entry list, not 24 observers.
 *
 * The 12% bottom inset means an element commits to its animation slightly
 * after it enters the viewport, so a fast scroll does not leave a trail of
 * half-played fades behind it.
 */
const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      revealObserver.unobserve(entry.target);
      play(entry.target);
    }
  },
  { rootMargin: '0px 0px -12% 0px', threshold: 0 }
);

function play(element) {
  const type = element.dataset.oneatAnimate || 'fade-up';
  const frames = KEYFRAMES[type];

  if (!frames) {
    element.classList.add('oneat-in');
    return;
  }

  const delay = Number.parseInt(element.dataset.oneatDelay || '0', 10) || 0;

  /**
   * Reveal-then-load looks broken when the image arrives after the fade: the
   * element animates in empty and the picture pops into it. Waiting for the
   * media (capped, so a slow CDN cannot strand the section invisible) is what
   * makes a card feel like it faded in whole.
   */
  mediaReady(element, 600).then(() => {
    const animation = element.animate(frames, {
      duration: DURATIONS[type] || 900,
      delay,
      easing: EASE,
      fill: 'backwards',
    });

    /* Drop the hiding class only once the animation owns the element, so there
       is no frame where CSS has released it but the fade has not begun. */
    element.classList.add('oneat-in');

    animation.finished.catch(() => {}).then(() => element.classList.add('oneat-animated'));
  });
}

/** Resolves when the element's images have decoded, or after `timeout` ms. */
function mediaReady(element, timeout) {
  const images = Array.from(element.querySelectorAll('img')).filter((img) => !img.complete);
  if (images.length === 0) return Promise.resolve();

  return Promise.race([
    Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })
      )
    ),
    new Promise((resolve) => setTimeout(resolve, timeout)),
  ]);
}

/* --------------------- word-staggered headings --------------------- */

/**
 * Wraps every word of a `[data-oneat-split]` heading in its own animating span
 * and staggers them, which is IMECO's `<split-words>` without Splitting.js.
 *
 * Only top-level text nodes are split, so inline markup a merchant typed into
 * a rich-text setting (a link, an <em>, a <strong>) survives as one unit
 * instead of being torn apart.
 */
function splitWords(element) {
  const base = Number.parseInt(element.dataset.oneatDelay || '0', 10) || 0;
  const type = element.dataset.oneatAnimate || 'fade-up';
  let index = 0;

  for (const node of Array.from(element.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const words = node.textContent.split(/(\s+)/);
      const fragment = document.createDocumentFragment();

      for (const word of words) {
        if (word.trim() === '') {
          fragment.append(word);
          continue;
        }
        fragment.append(wrapWord(word, type, base + index * 45));
        index += 1;
      }
      node.replaceWith(fragment);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      node.replaceWith(wrapWord(node, type, base + index * 45));
      index += 1;
    }
  }

  /* The heading itself must not also animate - its words do. */
  element.removeAttribute('data-oneat-animate');
  element.classList.add('oneat-in', 'oneat-split-ready');
}

function wrapWord(content, type, delay) {
  /* Two spans: the outer one clips, the inner one moves. Without the clip a
     `fade-up-large` word slides in over the line above it. */
  const clip = document.createElement('span');
  clip.className = 'oneat-word';

  const inner = document.createElement('span');
  inner.className = 'oneat-word__inner';
  inner.dataset.oneatAnimate = type;
  inner.dataset.oneatDelay = String(delay);
  inner.append(content);

  clip.append(inner);
  return clip;
}

/* ------------------------- image load states ------------------------- */

/**
 * Gives every lazily-loaded image the skeleton-then-fade treatment: the
 * wrapper shows a shimmer while the bytes are in flight, the image fades in
 * once decoded. This is IMECO's `is="lazy-image"`, minus the customised
 * built-in element (which Safari still does not support without a polyfill).
 *
 * `decode()` rather than the `load` event, so the fade starts on a frame where
 * the browser can actually paint the image.
 */
/**
 * The smallest box worth putting a loader in. The bar is 50px wide, so a 68px
 * product thumbnail cannot hold one without looking broken - and a swatch, a
 * badge or a payment mark has nothing to announce in the first place.
 */
const MIN_LOADER_PX = 120;

/**
 * Which images get the loader.
 *
 * This is IMECO's rule, arrived at the hard way. That theme only ever shows the
 * loader on an image inside a `.media` wrapper: its `LazyImage.connectedCallback`
 * bails when `closest('.media')` is null, and `product-thumbnail.liquid` has no
 * `.media` ancestor - so its thumbnails get nothing. It is a curated set, not
 * every lazy image on the page.
 *
 * The first version here matched `img[loading="lazy"]` blanket, which was wrong
 * in both directions on a product page:
 *
 * - the eight 68px gallery **thumbnails** are lazy, so each got a 50px bar
 *   inside a 68px button, appearing *after* the main image had already painted.
 *   That is the "images load, then a loader appears, then the thumbnails" the
 *   PDP was reported for;
 * - the **main gallery images** carry no `loading` attribute at all (Horizon
 *   leaves them eager), so they matched nothing and got no loader.
 *
 * Rather than a list of class names to keep in sync with every section - the
 * exact trap CLAUDE.md warns about - the rule is what the image *is*: content
 * media, big enough to hold the bar, not a control.
 */
function wantsLoader(img) {
  /* Explicit opt-in always wins, whatever its size or place. */
  if (img.closest('.oneat-media')) return true;

  /* Chrome, not content: the header logo, footer marks, drawer icons. */
  if (!img.closest('#MainContent')) return false;

  /* Controls: gallery thumbnails, swatches, carousel arrows. A control is a
     target to click, not media to wait for. */
  if (img.closest('button, [role="button"], .slideshow-control')) return false;

  /**
   * Proven-small images are skipped; unmeasurable ones are not.
   *
   * An image inside a skipped `content-visibility: auto` section - which is
   * most of the page below the fold, by design - measures 0x0 because its
   * subtree has not been laid out. Rejecting on `< MIN` would therefore strip
   * the loader from exactly the offscreen cards that need it most. So a zero
   * box gets the benefit of the doubt, and only a box we could actually
   * measure and found too small is rejected.
   */
  const rect = img.getBoundingClientRect();
  if (rect.width > 0 && rect.width < MIN_LOADER_PX) return false;
  if (rect.height > 0 && rect.height < MIN_LOADER_PX) return false;

  return true;
}

function watchImage(img) {
  if (img.dataset.oneatImageWatched) return;
  img.dataset.oneatImageWatched = 'true';

  const holder = img.closest('.oneat-media') || img.parentElement;

  if (img.complete && img.naturalWidth > 0) {
    img.classList.add('oneat-img-loaded');
    if (holder) holder.classList.remove('oneat-img-loading');
    return;
  }

  if (holder) {
    /**
     * The loader bar is two absolutely-positioned pseudo-elements on the
     * holder, so the holder has to be a positioned ancestor or they escape to
     * the nearest one - painting a single bar in the middle of the section
     * instead of one per image.
     *
     * Measured rather than set blanket: most of the theme's image wrappers are
     * already `position: relative`, and forcing it on the ones that are not
     * would re-parent any absolutely-positioned child they have (a badge, an
     * overlay) onto a different box.
     */
    if (getComputedStyle(holder).position === 'static') {
      holder.classList.add('oneat-img-anchor');
    }
    holder.classList.add('oneat-img-loading');
  }

  const settle = () => {
    img.classList.add('oneat-img-loaded');
    if (holder) holder.classList.remove('oneat-img-loading');
  };

  /**
   * `decode()` is only ever a refinement here, never a gate.
   *
   * Preferring it to the bare `load` event means the fade starts on a frame the
   * browser can actually paint the image on. But for an image the browser is
   * not rendering - a slideshow slide behind `display: none`, anything inside a
   * `content-visibility: auto` section that has been skipped - Chrome leaves the
   * promise **pending indefinitely**: measured on the home page's hero slides,
   * still unsettled after 800ms with `complete === true` and
   * `naturalWidth === 1536`. Chaining `settle` off it left 29 loader bars
   * spinning over images that had already arrived.
   *
   * So it is raced against a short timer: `decode()` wins on a normal image and
   * the timer wins on a non-rendered one, and either way the bar clears.
   */
  const onLoad = () => {
    if (!img.decode) return settle();

    Promise.race([
      img.decode().catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, 120)),
    ]).then(settle);
  };

  img.addEventListener('load', onLoad, { once: true });
  img.addEventListener('error', settle, { once: true });

  /**
   * The listeners above are attached after the `complete` check, so an image
   * that finished in between - the common case for a cached one - has already
   * fired `load` and would never be settled by anything. Re-check now that the
   * handler exists.
   */
  if (img.complete && img.naturalWidth > 0) onLoad();
}

/* ------------------------- automatic targets ------------------------- */

/**
 * What animates, declared in one place.
 *
 * The alternative was adding `data-oneat-animate` by hand to every section's
 * markup. That is fine for the custom `oneat-*` / `custom-*` sections, but the
 * home page also runs Horizon's own `product-list`, `collection-grid` and
 * `slideshow` - and editing those loses the edit on the next Horizon update
 * (CLAUDE.md rule 1). Matching by selector from here covers both kinds without
 * touching a single stock file, and it means the reveal behaviour of the whole
 * theme is one list to read rather than forty scattered attributes.
 *
 * `stagger` walks the matched elements inside each section and offsets each by
 * that many ms, capped, so a 24-card grid does not end with a card waiting a
 * second and a half to appear.
 *
 * A section can still opt out or override per-instance: an explicit
 * `data-oneat-animate` already on the element always wins, and
 * `data-oneat-no-animate` on any ancestor skips the subtree.
 */
const AUTO_TARGETS = [
  /* Section headings lead, so the eye lands on the copy before the media. */
  { selector: '.bc-heading, .ob-head, .oab-heading, .recently-viewed-heading', animate: 'fade-up' },

  /* Card grids and carousels - the bulk of every template. Class names are the
     real ones each section emits; see the id-suffix note in the matcher below.

     `collection-carousel` (`.cc-*`) is deliberately absent: that section already
     reveals its whole `.cc-container-*` with its own transition, and tagging the
     cards inside it too would run two fades over the same pixels. */
  { selector: '.product-grid__item', animate: 'fade-up', stagger: 55 },
  { selector: '.ocg-card', animate: 'fade-up', stagger: 55 },

  /* `hc-card-wrap` is the "Shop By Health Concern" tabbed carousel
     (`blocks/ai_gen_block_8927f58`). Cards in an unselected tab are display:none,
     so they measure as off-screen, get tagged, and reveal when their tab is
     opened - which is the behaviour we want anyway. */
  { selector: '.hc-card-wrap', animate: 'fade-up', stagger: 55 },

  /* `flip-testimonial` cards flip in 3D on hover, so this one is opacity-only:
     a translate on the card fights the flip's own transform and perspective. */
  { selector: '.ft-card', animate: 'fade-in', stagger: 80 },
  { selector: '.oc-meta-item, .cc-meta-item', animate: 'fade-up', stagger: 50 },
  { selector: '.bc-card, .ob-card', animate: 'fade-up', stagger: 70 },
  { selector: '.rv-product-card, .opc-card', animate: 'fade-up', stagger: 55 },

  /* The about page's icon row and its two editorial blocks. */
  { selector: '.oab-feature', animate: 'fade-up', stagger: 70 },
  { selector: '.oab-vision, .oab-checklist', animate: 'fade-up', stagger: 90 },

  /* The `about-us-*` sections. Disabled in the template today, but they are
     kept configured for rollback, so they are covered here too. */
  { selector: '.value-card, .team-member', animate: 'fade-up', stagger: 70 },
  { selector: '.story-image, .story-content', animate: 'fade-up', stagger: 90 },
];

const STAGGER_CAP = 420;

/**
 * Auto-tagging necessarily runs after the document has parsed, so anything it
 * hides has already been painted once - a visible flash, then a fade-in of
 * content the visitor was already reading.
 *
 * So on-screen elements are never auto-tagged. They keep their natural
 * appearance, which is also the right call for LCP: the largest element on the
 * page is above the fold by definition, and delaying it behind a 900ms fade
 * would make the theme measurably slower to make it feel faster.
 *
 * Elements authored with `data-oneat-animate` directly in Liquid are exempt -
 * their hidden state is in the markup, so it is applied in the first paint and
 * there is nothing to flash.
 */
function isOnScreen(element) {
  const rect = element.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0;
}

/**
 * Tags elements matching AUTO_TARGETS so the observer picks them up on the same
 * pass. Runs per section rather than per document so the stagger index restarts
 * in each section - otherwise the fifth section's first card would inherit the
 * accumulated delay of everything above it.
 */
function applyAutoTargets(scope) {
  const sections = scope.matches && scope.matches('.shopify-section')
    ? [scope]
    : Array.from(scope.querySelectorAll('.shopify-section'));

  /* A fragment handed in by a hydrating component has no section ancestor of
     its own; treat the fragment itself as one scope. */
  if (sections.length === 0) sections.push(scope);

  for (const section of sections) {
    for (const target of AUTO_TARGETS) {
      /* Class selectors are written unsuffixed above, but the custom sections
         suffix every class with the section id (`.cc-card-wrap-abc123`). Match
         both with a prefix selector per class. */
      const selector = target.selector
        .split(',')
        .map((part) => {
          const name = part.trim();
          return name.startsWith('.') ? `${name}, [class*="${name.slice(1)}"]` : name;
        })
        .join(', ');

      let index = 0;
      for (const element of section.querySelectorAll(selector)) {
        if (element.dataset.oneatAnimate != null) continue;
        if (element.closest('[data-oneat-no-animate]')) continue;
        /* Never animate something already inside an animating ancestor - the
           two transforms compound and the inner element overshoots. */
        if (element.parentElement && element.parentElement.closest('[data-oneat-animate]')) continue;
        if (isOnScreen(element)) continue;

        element.dataset.oneatAnimate = target.animate;
        if (target.stagger) {
          element.dataset.oneatDelay = String(Math.min(index * target.stagger, STAGGER_CAP));
        }
        index += 1;
      }
    }
  }
}

/* ------------------------- hover link prefetch ------------------------- */

/**
 * Warms the next page on hover / touch-start, the way IMECO's `instant-page.js`
 * does. On a normal store the gap between hover and click is 100-300ms, which
 * is most of a Shopify HTML response - so a collection or product page opens
 * with the document already in the HTTP cache.
 *
 * Deliberately conservative: same-origin GET navigations only, one prefetch per
 * URL, skipped on Save-Data or 2G, and capped so a long hover-scrub down a
 * collection grid cannot fan out into dozens of requests.
 */
function initPrefetch() {
  const connection = navigator.connection;
  if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;

  /* Feature-detect rather than assume: Safari shipped prefetch only recently. */
  const probe = document.createElement('link');
  if (!probe.relList || !probe.relList.supports || !probe.relList.supports('prefetch')) return;

  const seen = new Set();
  const LIMIT = 12;
  let timer = null;

  const candidate = (target) => {
    const anchor = target instanceof Element ? target.closest('a[href]') : null;
    if (!anchor) return null;
    if (anchor.target && anchor.target !== '_self') return null;
    if (anchor.hasAttribute('download') || anchor.dataset.noPrefetch != null) return null;

    let url;
    try {
      url = new URL(anchor.href, location.href);
    } catch (_) {
      return null;
    }

    if (url.origin !== location.origin) return null;
    if (url.pathname === location.pathname && url.search === location.search) return null;
    /* Cart and account mutate or are personalised - never warm them. */
    if (/^\/(cart|account|checkouts?)(\/|$)/.test(url.pathname)) return null;
    if (seen.has(url.href) || seen.size >= LIMIT) return null;

    return url.href;
  };

  const prefetch = (href) => {
    seen.add(href);
    const tag = document.createElement('link');
    tag.rel = 'prefetch';
    tag.href = href;
    tag.as = 'document';
    document.head.append(tag);
  };

  document.addEventListener(
    'pointerover',
    (event) => {
      const href = candidate(event.target);
      if (!href) return;
      /* 65ms of sustained hover, so sweeping the cursor across a nav does not
         prefetch every item it crosses. */
      clearTimeout(timer);
      timer = setTimeout(() => prefetch(href), 65);
    },
    { passive: true, capture: true }
  );

  document.addEventListener('pointerout', () => clearTimeout(timer), {
    passive: true,
    capture: true,
  });

  /* Touch has no hover, but touchstart still lands ~90ms before the click. */
  document.addEventListener(
    'touchstart',
    (event) => {
      const href = candidate(event.target);
      if (href) prefetch(href);
    },
    { passive: true, capture: true }
  );
}

/* ------------------------------ wiring ------------------------------ */

function scan(root) {
  const scope = root || document;

  if (!REDUCED && !DESIGN_MODE) {
    applyAutoTargets(scope);

    for (const element of scope.querySelectorAll('[data-oneat-split]:not(.oneat-split-ready)')) {
      splitWords(element);
    }
    for (const element of scope.querySelectorAll('[data-oneat-animate]:not(.oneat-in)')) {
      revealObserver.observe(element);
    }
  }

  /* Reads first, writes second. `wantsLoader` measures every candidate and
     `watchImage` then writes classes onto them; interleaving the two would
     invalidate layout between each measurement and thrash on an image-heavy
     collection page. */
  const candidates = [];
  for (const img of scope.querySelectorAll('img')) {
    if (!img.dataset.oneatImageWatched && wantsLoader(img)) candidates.push(img);
  }
  for (const img of candidates) watchImage(img);
}

/**
 * `content-visibility: auto` on offscreen sections is the single biggest
 * first-paint win available here - the browser skips layout and paint for
 * everything below the second section. It is released on first interaction, or
 * after 4s, because leaving it on permanently breaks in-page find and anchor
 * scrolling.
 */
function releaseDeferredRendering() {
  let done = false;
  const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];

  const release = () => {
    if (done) return;
    done = true;
    html.classList.remove('oneat-deferred-rendering');
    for (const type of events) window.removeEventListener(type, release);
  };

  for (const type of events) window.addEventListener(type, release, { passive: true, once: true });
  setTimeout(release, 4000);
}

if (DESIGN_MODE) {
  /* In the editor every section must be visible the moment it is added or
     re-rendered, and half-played reveals make a section look broken while the
     merchant edits it. Reveal everything, keep only the image fade. */
  html.classList.remove('oneat-motion', 'oneat-deferred-rendering');
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
} else {
  releaseDeferredRendering();
}

scan();

/**
 * Sections that hydrate late (a carousel, a paginated grid, quick-add markup,
 * a morphed cart drawer) arrive after the first scan. One observer over <body>
 * picks them up.
 *
 * Batched into a single rAF: Horizon morphs the cart drawer node by node, which
 * would otherwise fire a full scan per replaced line item, and `scan` does a
 * `getBoundingClientRect()` per candidate - a forced layout each time.
 */
let pending = new Set();
let frame = null;

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) pending.add(node);
    }
  }

  if (pending.size === 0 || frame !== null) return;

  frame = requestAnimationFrame(() => {
    frame = null;
    const batch = pending;
    pending = new Set();
    for (const node of batch) {
      /* A node detached again before the frame ran has nothing to reveal. */
      if (node.isConnected) scan(node);
    }
  });
}).observe(document.body, { childList: true, subtree: true });

initPrefetch();

export { scan };
